import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMe } from '~/api/auth';
import {
  type AttendanceDay,
  attendanceDaysQueryOptions,
  attendanceMeQueryOptions,
  attendanceSummaryQueryOptions,
  attendanceTodayQueryOptions,
  type TodayPerson,
} from '~/api/attendance';
import { useServerClock } from '~/api/serverClock';
import { AttendanceCalendar } from '~/features/attendance/AttendanceCalendar';
import { DailyBars } from '~/features/attendance/DailyBars';
import { AttendanceRegister, AttendanceTotals } from '~/features/attendance/EveryoneTables';
import { DaySheet } from '~/features/attendance/DaySheet';
import { useRange } from '~/features/attendance/RangeControl';
import { DAY_STATUS } from '~/features/attendance/status';
import { calendarDate, formatDayMonth, formatMinutes, formatTime } from '~/lib/format';
import { Avatar } from '~/ui/Avatar';
import { Bar } from '~/ui/Bar';
import { Card } from '~/ui/Card';
import { Donut } from '~/ui/Donut';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Sheet } from '~/ui/Sheet';
import { Skeleton } from '~/ui/Skeleton';
import { Tabs } from '~/ui/Tabs';

type View = 'mine' | 'everyone' | 'analysis';

export const Route = createFileRoute('/_app/attendance')({
  component: AttendancePage,
});

const monthBounds = (month: string): { from: string; to: string } => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { from: `${month}-01`, to };
};

function AttendancePage() {
  const me = useMe();
  const canSeeAll = me.person.role === 'owner' || me.person.role === 'manager';
  const [view, setView] = useState<View>('mine');

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader
        eyebrow="Attendance"
        title="Attendance"
        subtitle="Office hours from the attendance system: who came in, when, and for how long."
      />

      {canSeeAll && (
        <div className="mb-5" data-tour="att-view">
          <Tabs
            label="Attendance view"
            value={view}
            onChange={setView}
            options={[
              { value: 'mine', label: 'My attendance' },
              { value: 'everyone', label: 'Everyone' },
              { value: 'analysis', label: 'Analysis' },
            ]}
          />
        </div>
      )}

      {!canSeeAll || view === 'mine' ? (
        <MineView />
      ) : view === 'everyone' ? (
        <EveryoneView />
      ) : (
        <AnalysisView />
      )}
    </div>
  );
}

// ---- my attendance

function MineView() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const now = useServerClock(60_000);
  const today = calendarDate(now, tz);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [dayId, setDayId] = useState<string | null>(null);

  const query = useQuery(attendanceMeQueryOptions(monthBounds(month)));
  const data = query.data;
  const s = data?.person.summary;

  const onPick = (day: AttendanceDay) => {
    if (day.facts) setDayId(day.facts.id);
  };

  return (
    <>
      {query.isLoading && <Skeleton lines={6} />}
      {query.error && <ErrorNotice error={query.error} />}
      {data && s && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-tour="att-summary">
            <StatTile
              label="Present"
              value={`${s.presentDays}`}
              note={`of ${s.expectedDays} expected`}
            />
            <StatTile
              label="Late days"
              value={`${s.lateDays}`}
              note={`${s.totalLateMinutes}m total`}
              tone={s.lateDays > 0 ? 'late' : undefined}
            />
            <StatTile label="Avg / day" value={formatMinutes(s.avgWorkedMinutes)} note="worked" />
            <StatTile
              label="Attendance"
              value={s.attendanceRate === null ? '—' : `${Math.round(s.attendanceRate * 100)}%`}
              note="of expected days"
            />
          </div>
          <Card title="Calendar" eyebrow="This month" dataTour="att-month">
            <AttendanceCalendar
              month={month}
              days={data.days}
              today={today}
              onMonth={setMonth}
              onPick={onPick}
            />
            <Legend />
          </Card>
        </div>
      )}
      <DaySheet dayId={dayId} onClose={() => setDayId(null)} />
    </>
  );
}

// ---- everyone (managers and owner)

function EveryoneView() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const now = useServerClock(60_000);
  const today = calendarDate(now, tz);
  const [people, setPeople] = useState<'days' | 'totals'>('days');
  const [dayId, setDayId] = useState<string | null>(null);
  const [who, setWho] = useState<{ id: string; name: string } | null>(null);
  const { control, range } = useRange(today);
  const today$ = useQuery(attendanceTodayQueryOptions);
  const summary = useQuery({
    ...attendanceSummaryQueryOptions(range),
    enabled: people === 'totals',
  });

  const board = today$.data;

  return (
    <div className="flex flex-col gap-6">
      <Card title="Today" eyebrow={today}>
        {today$.isLoading && <Skeleton lines={3} />}
        {today$.error && <ErrorNotice error={today$.error} />}
        {board && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile label="In" value={`${board.counts.in}`} />
              <StatTile label="Left" value={`${board.counts.out}`} />
              <StatTile
                label="Not in"
                value={`${board.counts.notIn}`}
                tone={board.counts.notIn > 0 ? 'late' : undefined}
              />
              <StatTile label="Off" value={`${board.counts.off}`} />
            </div>
            {board.people.length === 0 ? (
              <p className="text-sm text-ink-3">No one is mapped to attendance yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line">
                {board.people.map((p) => (
                  <TodayRow key={p.personId} person={p} tz={tz} />
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      <Card
        title={people === 'days' ? 'Day by day' : 'Totals per person'}
        eyebrow={`${formatDayMonth(range.from)} to ${formatDayMonth(range.to)}`}
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {control}
          <Tabs
            label="Show"
            value={people}
            onChange={setPeople}
            options={[
              { value: 'days', label: 'Day by day' },
              { value: 'totals', label: 'Totals per person' },
            ]}
          />
        </div>
        {people === 'days' ? (
          <AttendanceRegister range={range} onOpenDay={setDayId} onOpenPerson={setWho} />
        ) : summary.error ? (
          <ErrorNotice error={summary.error} />
        ) : (
          <AttendanceTotals
            people={summary.data?.people ?? []}
            loading={summary.isPending}
            onOpenPerson={setWho}
          />
        )}
      </Card>

      <DaySheet dayId={dayId} onClose={() => setDayId(null)} />
      <PersonMonthSheet who={who} onClose={() => setWho(null)} today={today} />
    </div>
  );
}

function PersonMonthSheet({
  who,
  onClose,
  today,
}: {
  who: { id: string; name: string } | null;
  onClose: () => void;
  today: string;
}) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const [dayId, setDayId] = useState<string | null>(null);
  const range = monthBounds(month);
  const query = useQuery({
    ...attendanceDaysQueryOptions({ ...range, personId: who?.id ?? '' }),
    enabled: who !== null,
  });

  return (
    <>
      <Sheet open={who !== null} onClose={onClose} eyebrow="Attendance" title={who?.name ?? ''}>
        {query.isLoading && <Skeleton lines={6} />}
        {query.error && <ErrorNotice error={query.error} />}
        {query.data && (
          <>
            <AttendanceCalendar
              month={month}
              days={query.data.days}
              today={today}
              onMonth={setMonth}
              onPick={(d) => d.facts && setDayId(d.facts.id)}
            />
            <Legend />
          </>
        )}
      </Sheet>
      <DaySheet dayId={dayId} onClose={() => setDayId(null)} />
    </>
  );
}

function TodayRow({ person, tz }: { person: TodayPerson; tz: string }) {
  const STATE: Record<
    TodayPerson['state'],
    { label: string; tone: 'done' | 'warn' | 'late' | 'neutral' }
  > = {
    in: { label: 'In', tone: 'done' },
    out: { label: 'Left', tone: 'neutral' },
    not_in: { label: 'Not in', tone: 'late' },
    off: { label: 'Off', tone: 'neutral' },
  };
  const st = STATE[person.state];
  return (
    <li className="flex items-center gap-3 py-2.5">
      <Avatar name={person.name} size="sm" personId={person.personId} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm">{person.name}</span>
          {person.late && <Pill tone="late">Late {person.lateMinutes}m</Pill>}
          {person.unverified && <Pill tone="warn">Unverified</Pill>}
        </div>
        {person.firstInAt && (
          <div className="text-xs text-ink-3">
            In {formatTime(person.firstInAt, tz)} · {formatMinutes(person.workedMinutes)} worked
          </div>
        )}
      </div>
      <Pill tone={st.tone}>{st.label}</Pill>
    </li>
  );
}

// ---- analysis (managers and owner)

function AnalysisView() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const now = useServerClock(60_000);
  const today = calendarDate(now, tz);
  const { control, range } = useRange(today);
  const summary = useQuery(attendanceSummaryQueryOptions(range));
  const sum = summary.data;
  const t = sum?.totals;

  return (
    <div className="flex flex-col gap-6">
      <div>{control}</div>

      {summary.isLoading && <Skeleton lines={6} />}
      {summary.error && <ErrorNotice error={summary.error} />}

      {sum && t && (
        <>
          <div className="grid gap-4 md:grid-cols-[auto_1fr]">
            <Card title="Attendance rate" eyebrow={`${range.from} to ${range.to}`}>
              <div className="flex items-center gap-4">
                <Donut
                  percent={t.attendanceRate === null ? 0 : Math.round(t.attendanceRate * 100)}
                  label="present"
                  segments={[
                    { value: t.presentDays, color: 'var(--done)', label: 'Present' },
                    { value: t.incompleteDays, color: 'var(--warn)', label: 'No checkout' },
                    { value: t.absentDays, color: 'var(--late)', label: 'Absent' },
                  ]}
                />
                <ul className="flex flex-col gap-1 text-sm">
                  <LegendRow tone="done" label="Present" value={t.presentDays} />
                  <LegendRow tone="warn" label="No checkout" value={t.incompleteDays} />
                  <LegendRow tone="late" label="Absent" value={t.absentDays} />
                  <LegendRow tone="neutral" label="Expected" value={t.expectedDays} />
                  {t.notUploadedDays > 0 && (
                    <LegendRow tone="neutral" label="Not uploaded" value={t.notUploadedDays} />
                  )}
                </ul>
              </div>
              {t.notUploadedDays > 0 && (
                <NotUploadedHint dates={sum.daily.filter((d) => !d.uploaded).map((d) => d.date)} />
              )}
            </Card>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatTile
                label="Late days"
                value={`${t.lateDays}`}
                note={`${t.totalLateMinutes}m total`}
                tone={t.lateDays > 0 ? 'late' : undefined}
              />
              <StatTile label="Avg / day" value={formatMinutes(t.avgWorkedMinutes)} note="worked" />
              <StatTile
                label="Overtime"
                value={formatMinutes(t.overtimeMinutes)}
                note="over schedule"
              />
              <StatTile label="Early leave" value={`${t.earlyLeaveDays}`} note="days" />
              <StatTile
                label="Unverified"
                value={`${t.unverifiedDays}`}
                note="need a look"
                tone={t.unverifiedDays > 0 ? 'late' : undefined}
              />
              <StatTile label="Worked" value={formatMinutes(t.workedMinutes)} note="total" />
            </div>
          </div>

          <Card title="Each day" eyebrow="Present, no checkout, absent">
            <DailyBars points={sum.daily} today={today} />
            <div className="mt-3 flex flex-wrap gap-2">
              <Pill tone="done">Present</Pill>
              <Pill tone="warn">No checkout</Pill>
              <Pill tone="late">Absent</Pill>
            </div>
          </Card>

          {sum.departments.length > 1 && (
            <Card title="By department" eyebrow={`${range.from} to ${range.to}`}>
              <ul className="flex flex-col gap-3">
                {sum.departments.map((d) => {
                  const rate = d.summary.attendanceRate;
                  return (
                    <li key={d.departmentId ?? 'none'} className="flex items-center gap-3">
                      <div className="w-40 min-w-0">
                        <div className="truncate text-sm font-medium">{d.name}</div>
                        <div className="text-xs text-ink-3">{d.personCount} people</div>
                      </div>
                      <div className="flex-1">
                        <Bar
                          value={rate === null ? 0 : rate * 100}
                          max={100}
                          tone={
                            rate !== null && rate >= 0.9
                              ? 'done'
                              : rate !== null && rate >= 0.75
                                ? 'warn'
                                : 'late'
                          }
                        />
                      </div>
                      <div className="w-16 text-right font-mono text-sm tabular">
                        {rate === null ? '—' : `${Math.round(rate * 100)}%`}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function LegendRow({
  tone,
  label,
  value,
}: {
  tone: 'done' | 'warn' | 'late' | 'neutral';
  label: string;
  value: number;
}) {
  const dot = { done: 'bg-done', warn: 'bg-warn', late: 'bg-late', neutral: 'bg-ink-3' }[tone];
  return (
    <li className="flex items-center gap-2">
      <span className={`size-2.5 rounded-full ${dot}`} aria-hidden />
      <span className="text-ink-2">{label}</span>
      <span className="ml-auto font-mono tabular">{value}</span>
    </li>
  );
}

// ---- shared bits

function Legend() {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {(['present', 'incomplete', 'absent', 'off'] as const).map((s) => (
        <Pill key={s} tone={DAY_STATUS[s].tone}>
          {DAY_STATUS[s].label}
        </Pill>
      ))}
    </div>
  );
}

function StatTile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'late';
}) {
  return (
    <Card padded={false} className="px-4 py-3">
      <div className="text-xs text-ink-3">{label}</div>
      <div className={`font-mono text-2xl tabular ${tone === 'late' ? 'text-late' : ''}`}>
        {value}
      </div>
      {note && <div className="text-xs text-ink-3">{note}</div>}
    </Card>
  );
}

/**
 * Working days with no attendance brought in yet. They are not counted as
 * absences; the owner uploads the Attendo export to fill them.
 */
function NotUploadedHint({ dates }: { dates: string[] }) {
  const me = useMe();
  const shown = dates.slice(0, 6).map(formatDayMonth).join(', ');
  return (
    <p className="mt-4 rounded-control border border-dashed border-line bg-paper-2 px-3 py-2 text-xs text-ink-2">
      Attendance not uploaded for {dates.length} day{dates.length === 1 ? '' : 's'}: {shown}
      {dates.length > 6 ? '…' : ''}. These are not counted as absences.{' '}
      {me.person.role === 'owner' ? (
        <Link
          to="/settings"
          search={{ section: 'attendance' }}
          className="text-accent hover:underline"
        >
          Upload the Attendo export
        </Link>
      ) : (
        'The owner can upload them.'
      )}
    </p>
  );
}
